import ModalViewModel, {
  ModalViewModelClosingSignal,
} from 'infra/ModalViewModel'
import { action, computed, makeObservable } from 'mobx'
import { PromptQuestionViewModelPort } from 'jobs/prompts/ports'
import { Prompt } from 'jobs/prompts/Prompt'
import { BehaviorSubject, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { fromStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'
import { task } from 'mobx-task'
import { AlertFactory, AlertState } from 'alerts/Alert'

export default class ReviewPromptDialogViewModel extends ModalViewModel {
  /**
   * Tracks the current prompt being viewed.
   */
  private readonly promptSubject = new BehaviorSubject<Prompt | null>(null)

  /**
   * Question view model for the prompt.
   *
   * @private
   */
  private readonly questionVM$ = this.promptSubject.pipe(
    switchMap((prompt) =>
      !prompt
        ? of(null)
        : prompt.answer$.pipe(
            // This ensures that if the answer changes in realtime,
            // the question view model is recreated with the prompt answer filled in.
            map(() => this.adapter.createQuestionViewModel(prompt)),
          ),
    ),
    // Keep the pipeline running while the dialog is showing
    this.whileShowing(),
    shareLatest(),
  )

  /**
   * Streams to MobX observables.
   */
  private readonly $ = {
    prompt: fromStream(null, this.promptSubject),
    questionVM: fromStream(null, this.questionVM$),
  }

  /**
   * Deletes the prompt
   */
  private _deletePrompt = async () => {
    if (!this.questionVM) {
      return
    }

    if (!this.prompt?.canDelete) {
      return
    }

    try {
      await this.adapter.deletePrompt({
        promptId: this.prompt.id,
      })
    } catch (e) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(`Failed to delete Prompt`, e.message)
        .autoDismiss()
        .show()
    }

    this.close()
  }

  /**
   * Dialog for deleting a prompt
   */
  deletePromptVM = new DeletePromptDialogViewModel(
    this._deletePrompt.bind(this),
  )

  /**
   * Constructor.
   */
  constructor(
    private adapter: PromptQuestionViewModelPort,
    private newAlert: AlertFactory,
    closingSignal?: ModalViewModelClosingSignal,
  ) {
    super(closingSignal)
    makeObservable(this)
  }

  /**
   * The current prompt.
   */
  @computed
  get prompt() {
    return this.$.prompt()
  }

  /**
   * The question view model for the prompt.
   */
  @computed
  get questionVM() {
    return this.$.questionVM()
  }

  /**
   * Sets the question VM and displays the dialog.
   *
   * @param prompt
   * @returns
   */
  @action.bound
  viewPrompt(prompt: Prompt) {
    if (!prompt.question) {
      return
    }

    this.promptSubject.next(prompt)
    this.show()
  }

  @action.bound
  answerPrompt = task.resolved(async () => {
    if (!this.questionVM) {
      return
    }

    const validationResult = this.questionVM.validateResponse()

    if (validationResult?.type !== 'Success') {
      return
    }

    if (!this.prompt?.canAnswer) {
      return
    }

    try {
      await this.adapter.answerPrompt({
        promptId: this.prompt.id,
        answer: this.questionVM.toInputValue(),
      })
    } catch (e) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(`Failed to answer Prompt`, e.message)
        .autoDismiss()
        .show()
    }

    this.close()
  })
}

export class DeletePromptDialogViewModel extends ModalViewModel {
  /**
   * Constructor.
   */
  constructor(
    private deletePromptDelegate: () => Promise<void>,
    closingSignal?: ModalViewModelClosingSignal,
  ) {
    super(closingSignal)
  }

  deletePrompt = task.resolved(async () => {
    await this.deletePromptDelegate()
    this.close()
  })
}
